import classNames from 'classnames';
import React, { Ref, useEffect, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import Input from '@alfa-retail-react/components/input';
import Button from '@alfa-retail-react/components/button';
import { useForm, useNavigate, Plate } from '@alfa-retail-react/components';

import { WEB_VISOR_DISABLE, WEB_VISOR_HIDE } from 'app/utils/AnalyticsUtils';
import { FormatUtils } from 'app/utils/FormatUtils';
import { validationExceptionHandler } from 'app/logic/pages/error/validationExceptionHandler';
import { unauthorizedExceptionHandlerOnSubmitData } from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';
import { networkExceptionHandlerOnSubmitData } from 'app/logic/pages/error/networkExceptionHandler';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { SmsSignFormStore } from 'app/logic/pages/sign/SmsSignFormStore';
import { OtpValidationException } from 'app/api/exception/OtpValidationException';
import { getExceptionHandler } from 'app/api/getExceptionHandler';
import { orderRejectedExceptionHandler } from 'app/logic/pages/error/orderRejectedExceptionHandler';
import { ERROR_PAGE_URL } from 'app/logic/pages/error/ErrorPage';
import { ActionTimer } from 'app/logic/pages/sign/ActionTimer';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { MessageNotComing } from 'app/logic/pages/sign/MessageNotComing';
import { OtpConfirmationStatus } from 'app/model/OtpConfirmationStatus';
import { ReturnAnalyticsType } from 'app/analytics/useAnalytics';
import { ValidationRules } from 'app/form/ValidationRules';
import { AnalyticsActionTypeName } from 'app/analytics/AnalyticsActionTypeName';
import { useI18nContext } from 'app/store/сontext/I18nContext';

import 'app/logic/pages/sign/SmsSigningForm.less';

interface SmsSigningFormProps {
    /**
     * Стор страницы подтверждения подписания документов.
     */
    formStore: SmsSignFormStore;

    analytics: ReturnAnalyticsType;
}

type SmsSigningFormValues = {
    otp: string;
};

const GENERATION_COUNT_ERROR_MESSAGE =
    'Закончились попытки запроса нового кода';
const SENT_INFO_MESSAGE = 'Отправлено новое СМС';
const OTP_FIELD_PLACEHOLDER = 'Код из сообщения';
const OTP_CONFIRMATION_FAILED_TITLE = 'Не удалось выполнить операцию';
const OTP_CONFIRMATION_FAILED_MESSAGE =
    'Превышено количество попыток ввода кода из СМС. Создайте новую заявку';

const OTP_FORM_ITEM_NAME = 'otp';

const getPhoneInfoMsg = (phone: string) => `Мы отправили код на номер ${phone}`;

/**
 * Компонент формы подписания через СМС.
 */
export const SmsSigningForm = observer(
    ({ formStore, analytics }: SmsSigningFormProps) => {
        const navigate = useNavigate();

        /**
         * в случае значения initial не показывается сообщение "отправлено новое смс" при работающем таймере
         */
        const [timer, setTimer] = useState<'initial' | boolean>('initial');
        const notification = useNotificationContext();
        const applicationContext = useApplicationContext();
        const i18nContext = useI18nContext();

        const form = useForm<SmsSigningFormValues>({});
        const {
            handleSubmitWithValidationError,
            control,
            pageErrors,
            setPageErrors,
            pageErrorsComponentRef,
            setError,
            handleMultipleErrors,
            setFieldElement,
            trigger,
        } = form;
        const fieldHandler = new InputFieldEventHandler<SmsSigningFormValues>(
            trigger,
        );

        const onSubmit = async () => {
            setPageErrors([]);
            analytics.confirm();
            formStore
                .completeOrder()
                .then(() => {
                    analytics.resolve(AnalyticsActionTypeName.CONFIRM);
                    navigate('/result');
                })
                .catch(
                    exceptionLogger(
                        'SmsSigningForm',
                        'formStore.completeOrder',
                    ),
                )
                .catch(
                    unauthorizedExceptionHandlerOnSubmitData({
                        navigate,
                        phoneNumber: applicationContext.phoneNumber,
                    }),
                )
                .catch(
                    getExceptionHandler(
                        OtpValidationException.NAME,
                        (error: OtpValidationException) => {
                            runInAction(
                                () =>
                                    (formStore.otpConfirmation =
                                        error.confirmation),
                            );
                            setError(OTP_FORM_ITEM_NAME, {
                                message: error.message,
                            });
                        },
                    ),
                )
                .catch(
                    orderRejectedExceptionHandler(
                        navigate,
                        analytics,
                        i18nContext.t,
                    ),
                )
                .catch(validationExceptionHandler(form, analytics))
                .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
                .catch(defaultExceptionHandler(notification, analytics));
        };

        useEffect(() => {
            if (
                (!formStore.hasGenerationAttempts &&
                    !formStore.hasConfirmationAttempts) ||
                (!formStore.hasGenerationAttempts &&
                    formStore.otpConfirmation.status ===
                        OtpConfirmationStatus.EXPIRED)
            ) {
                analytics.fullEvent('SmsAttemptsExhaustedEventOccurred');
                navigate(ERROR_PAGE_URL, {
                    title: OTP_CONFIRMATION_FAILED_TITLE,
                    messages: [OTP_CONFIRMATION_FAILED_MESSAGE],
                });
            }

            // Автогенерация при первышении попыток подтвреждения кода.
            // if (formStore.hasGenerationAttempts && !formStore.hasConfirmationAttempts) {
            // setTimer(false);
            // formStore.getOrderConfirmationOtp()
            //     .then(() => setTimer(true));
            // }
        }, [
            formStore.otpConfirmation?.confirmationCount,
            formStore.otpConfirmation?.generationCount,
        ]);

        // Альтернативный сценарий 1с
        // useEffect(() => {
        //     if (!formStore.hasGenerationAttempts && formStore.lastConfirmationAttemp && !timer) {
        //         navigate('/error', {message: OTP_CONFIRMATION_FAILED});
        //     }
        // }, [timer])

        const maskedPhone = FormatUtils.toMaskedPhoneNumberString(
            formStore.order.client.mobilePhone,
        );

        const isSentInfoMessageShown =
            timer === true &&
            formStore.otpConfirmation.status === OtpConfirmationStatus.OK;

        return (
            <div className="smsSigningForm">
                {!!formStore.otpConfirmation && (
                    <div
                        className={classNames(
                            'secondaryMessage',
                            WEB_VISOR_HIDE,
                        )}
                    >
                        {getPhoneInfoMsg(maskedPhone)}
                    </div>
                )}
                <FormProvider<{ [OTP_FORM_ITEM_NAME]: string }> {...form}>
                    <form
                        onSubmit={handleSubmitWithValidationError({
                            onValid: onSubmit,
                        })}
                    >
                        <Controller<SmsSigningFormValues>
                            name={OTP_FORM_ITEM_NAME}
                            control={control}
                            rules={{ required: ValidationRules.required() }}
                            render={({ field, fieldState }) => {
                                const { onBlur, onClear, onChange } =
                                    fieldHandler.getEventHandlers(
                                        field,
                                        OTP_FORM_ITEM_NAME,
                                        formStore.setOtp,
                                    );

                                return (
                                    <Input
                                        block
                                        clear
                                        ref={(element) => {
                                            field.ref(element);
                                            setFieldElement(
                                                OTP_FORM_ITEM_NAME,
                                                element,
                                            );
                                        }}
                                        label={OTP_FIELD_PLACEHOLDER}
                                        className={OTP_FORM_ITEM_NAME}
                                        inputClassName={WEB_VISOR_DISABLE}
                                        value={field.value}
                                        size="l"
                                        error={handleMultipleErrors(
                                            fieldState.error,
                                        )}
                                        onBlur={onBlur}
                                        onClear={onClear}
                                        onChange={onChange}
                                    />
                                );
                            }}
                        />
                        {!!formStore.otpConfirmation && (
                            <>
                                {!formStore.hasGenerationAttempts && (
                                    <Plate
                                        className={'generationCountError'}
                                        view="attention"
                                    >
                                        {GENERATION_COUNT_ERROR_MESSAGE}
                                    </Plate>
                                )}
                                {isSentInfoMessageShown && (
                                    <Plate
                                        className={'otpConfirmationOk'}
                                        view="positive"
                                    >
                                        {SENT_INFO_MESSAGE}
                                    </Plate>
                                )}
                            </>
                        )}
                        <ActionTimer
                            formStore={formStore}
                            timer={timer}
                            setTimer={setTimer}
                            clearFieldErrors={() =>
                                setError(OTP_FORM_ITEM_NAME, null)
                            }
                        />
                        {pageErrors.length > 0 && (
                            <PageErrors
                                ref={
                                    pageErrorsComponentRef as Ref<HTMLDivElement>
                                }
                                errors={pageErrors}
                            />
                        )}
                        <Button
                            className="submitButton"
                            view="accent"
                            type="submit"
                            block
                            disabled={formStore.actionStateStore.isLoading}
                        >
                            Подписать документы
                        </Button>
                    </form>
                </FormProvider>

                <MessageNotComing
                    onModalOpen={() =>
                        analytics.common('SmsNotReceivedActionInitiated')
                    }
                />
            </div>
        );
    },
);
